import { FormPlayer } from '@/components/FormPlayer/FormPlayer'
import { FormPreviewShell } from '@/components/FormPreview/FormPreviewShell'
import { Badge } from '@/components/ui/badge'
import { api } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { createPublicFormReturnToFromLocation } from '@/lib/auth-return-to'
import { getFormPreviewSnapshot, getPublicForm } from '@/lib/data-client/forms'
import { primaryFormId } from '@/lib/mock-data'
import { useQuery } from '@tanstack/react-query'
import { Navigate, useParams } from '@tanstack/react-router'

function isPublicPreviewRequest(searchParams: URLSearchParams) {
  const previewValue = searchParams.get('preview')?.toLowerCase()
  const modeValue = searchParams.get('mode')?.toLowerCase()
  const viewValue = searchParams.get('view')?.toLowerCase()
  const sourceValue = searchParams.get('source')?.toLowerCase()

  return (
    (searchParams.has('preview') &&
      (previewValue === '' || previewValue === '1' || previewValue === 'true')) ||
    modeValue === 'preview' ||
    viewValue === 'preview' ||
    sourceValue === 'preview' ||
    sourceValue === 'public_preview'
  )
}

export default function PublicFormRoute() {
  const params = useParams({ strict: false }) as { formId?: string }
  const formId = params.formId ?? primaryFormId
  const isPreview = isPublicPreviewRequest(new URLSearchParams(window.location.search))
  const { isAuthenticated, isLoading } = useAuth()
  const previewQuery = useQuery({
    queryKey: ['form-preview-snapshot', formId, 'public_preview'],
    queryFn: () => getFormPreviewSnapshot(formId, { source: 'public_preview' }),
    enabled: isPreview,
  })
  const accessQuery = useQuery({
    queryKey: ['public-form-access', formId],
    queryFn: () => api.publicFormAccess(formId),
    enabled: !isPreview && !isLoading && isAuthenticated,
  })
  const publicFormQuery = useQuery({
    queryKey: ['public-form', formId],
    queryFn: () => getPublicForm(formId),
    enabled: !isPreview && accessQuery.data?.allowed === true,
  })

  if (isPreview) {
    if (previewQuery.isError) {
      return (
        <div className="grid min-h-dvh place-items-center bg-slate-100 px-5 text-center">
          <div>
            <Badge variant="destructive">preview error</Badge>
            <h1 className="mt-4 text-xl font-semibold text-slate-950">無法載入預覽</h1>
            <p className="mt-2 text-sm text-slate-500">
              請回到問卷管理確認此問卷的 mock-safe snapshot 是否存在。
            </p>
          </div>
        </div>
      )
    }

    if (previewQuery.isLoading || !previewQuery.data) {
      return (
        <div className="grid min-h-dvh place-items-center bg-slate-100 px-5 text-center">
          <div>
            <Badge variant="secondary">preview-only</Badge>
            <h1 className="mt-4 text-xl font-semibold text-slate-950">正在載入完整預覽</h1>
            <p className="mt-2 text-sm text-slate-500">
              這裡只讀取 mock-safe preview snapshot，不會建立填答紀錄。
            </p>
          </div>
        </div>
      )
    }

    return (
      <main className="h-dvh overflow-hidden bg-slate-100 p-2 sm:p-3">
        <FormPreviewShell snapshot={previewQuery.data} mode="full_flow" />
      </main>
    )
  }

  if (isLoading) {
    return (
      <div className="grid min-h-dvh place-items-center text-sm text-slate-500">
        確認登入狀態...
      </div>
    )
  }

  if (!isAuthenticated) {
    const returnTo = createPublicFormReturnToFromLocation(window.location)
    return <Navigate to="/login" search={{ returnTo } as never} replace />
  }

  if (accessQuery.isError) {
    return (
      <main className="grid min-h-dvh place-items-center bg-slate-50 p-6 text-center">
        <div className="max-w-md rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <Badge variant="secondary">無法確認資格</Badge>
          <h1 className="mt-4 text-xl font-semibold text-slate-950">目前無法確認填寫資格。</h1>
          <p className="mt-2 text-sm leading-6 text-slate-500">請稍後重新開啟問卷連結。</p>
        </div>
      </main>
    )
  }

  if (accessQuery.isLoading || !accessQuery.data) {
    return (
      <div className="grid min-h-dvh place-items-center text-sm text-slate-500">
        確認填寫資格...
      </div>
    )
  }

  if (!accessQuery.data.allowed) {
    const message =
      accessQuery.data.message ??
      (accessQuery.data.code === 'login_required'
        ? '請先登入後再填寫問卷。'
        : '此帳號目前不可填寫問卷。')
    return (
      <main className="grid min-h-dvh place-items-center bg-slate-50 p-6 text-center">
        <div className="max-w-md rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <Badge variant="secondary">無法填寫</Badge>
          <h1 className="mt-4 text-xl font-semibold text-slate-950">{message}</h1>
          <p className="mt-2 text-sm leading-6 text-slate-500">
            請確認登入帳號是否具備填寫此問卷的資格。
          </p>
        </div>
      </main>
    )
  }

  if (publicFormQuery.isLoading) {
    return (
      <div className="grid min-h-dvh place-items-center text-sm text-slate-500">載入問卷...</div>
    )
  }

  if (publicFormQuery.isError || !publicFormQuery.data) {
    return (
      <main className="grid min-h-dvh place-items-center bg-slate-50 p-6 text-center">
        <div className="max-w-md rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <Badge variant="secondary">找不到問卷</Badge>
          <h1 className="mt-4 text-xl font-semibold text-slate-950">找不到問卷</h1>
          <p className="mt-2 text-sm leading-6 text-slate-500">
            這份問卷可能尚未公開、已封存，或目前不開放填寫。
          </p>
        </div>
      </main>
    )
  }

  return <FormPlayer form={publicFormQuery.data} />
}
